import { Box, Grid, Typography, LinearProgress, Chip, Button, Paper, TextField } from '@mui/material';
import {
  VolumeUp,
  Block,
  LockOpen,
  RestartAlt,
  Delete,
  Star,
  StarBorder,
  LocationOn,
  BatteryChargingFull,
  BatteryFull
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Scooter } from '../../../resources/types/scootersTypes';
import { useMemo, useState } from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { selectCities } from '../../../redux/slices/citiesSlice';
import { useGetCoordsHistoriesQuery } from '../../../redux/api/scootersApi';

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px'
};

const defaultCenter = {
  lat: 48.9225224,
  lng: 24.7103188
};

interface Props {
  scooter?: Scooter;
  isSuperAdmin: boolean;
  onSignal: () => void;
  onLockToggle: () => void;
  onRestart: () => void;
  onDelete: () => void;
  onBatteryCover?: (value: boolean) => void;
}

const InformationContent = ({ 
  scooter, 
  isSuperAdmin,
  onSignal,
  onLockToggle,
  onRestart,
  onDelete,
  onBatteryCover
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { items: cities } = useSelector(selectCities);
  const [selectedDate, setSelectedDate] = useState<string>(moment().format('YYYY-MM-DD'));
  
  const dateFrom = moment(selectedDate).startOf('day').toISOString();
  const dateTo = moment(selectedDate).endOf('day').toISOString();
  
  const { data: coordsHistory = [] } = useGetCoordsHistoriesQuery(
    scooter?._id ? { 
      dateFrom, 
      dateTo, 
      scooterId: scooter._id 
    } : undefined!,
    { skip: !scooter?._id }
  );

  const routePath = useMemo(() => {
    return coordsHistory.flatMap(history => 
      history.coords.map(coord => ({ lat: coord.lat, lng: coord.lon }))
    );
  }, [coordsHistory]);

  const mapCenter = scooter?.coords?.lat && scooter?.coords?.lon
    ? { lat: scooter.coords.lat, lng: scooter.coords.lon }
    : defaultCenter;

  const mapOptions = {
    styles: theme.palette.mode === 'dark' ? [
      { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
      {
        featureType: 'administrative.locality',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }]
      },
      {
        featureType: 'poi',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }]
      },
      {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [{ color: '#263c3f' }]
      },
      {
        featureType: 'poi.park',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#6b9a76' }]
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ color: '#38414e' }]
      },
      {
        featureType: 'road',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#212a37' }]
      },
      {
        featureType: 'road',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#9ca5b3' }]
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [{ color: '#746855' }]
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#1f2835' }]
      },
      {
        featureType: 'road.highway',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#f3d19c' }]
      },
      {
        featureType: 'transit',
        elementType: 'geometry',
        stylers: [{ color: '#2f3948' }]
      },
      {
        featureType: 'transit.station',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }]
      },
      {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#17263c' }]
      },
      {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#515c6d' }]
      },
      {
        featureType: 'water',
        elementType: 'labels.text.stroke',
        stylers: [{ color: '#17263c' }]
      }
    ] : []
  };

  const getStatusLabel = (status?: string) => {
    const statusMap: Record<string, string> = {
      'available': t('scooterModal.statusActiveForRent'),
      'transporting': t('scooterModal.statusTransporting'),
      'charging': t('scooterModal.statusCharging'),
      'maintenance': t('scooterModal.statusMaintenance'),
      'broken_in_city': t('scooterModal.statusBrokenInCity'),
      'low_battery': t('scooterModal.statusLowBattery'),
      'hidden': t('scooterModal.statusHidden')
    };
    return statusMap[status?.toLowerCase() || ''] || status || t('scooterModal.statusUnknown');
  };

  const getStatusColor = (status?: string) => {
    const colorMap: Record<string, string> = {
      'available': '#4CAF50',
      'transporting': '#2196F3',
      'charging': '#FF9800',
      'maintenance': '#9C27B0',
      'broken_in_city': '#F44336',
      'low_battery': '#FF5722',
      'hidden': '#757575'
    };
    return colorMap[status?.toLowerCase() || ''] || '#757575';
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    
    for (let i = 0; i < 5; i++) {
      stars.push(
        i < fullStars ? (
          <Star key={i} sx={{ color: '#FFA726', fontSize: '1.2rem' }} />
        ) : (
          <StarBorder key={i} sx={{ color: '#FFA726', fontSize: '1.2rem' }} />
        )
      );
    }
    return stars;
  };

  const hasInactivityFlag = (battery: number | string | undefined) => {
    return String(battery || '').startsWith('*');
  };

  const cleanBatteryValue = (battery: number | string | undefined) => {
    if (battery === null || battery === undefined) return battery;
    const batteryStr = String(battery);
    if (batteryStr.startsWith('*')) {
      const cleaned = batteryStr.substring(1);
      const num = Number(cleaned);
      return Number.isNaN(num) ? 0 : num;
    }
    return battery;
  };

  const cityName = useMemo(() => {
    if (!scooter?.city) return t('scooterModal.cityUnknown');
    
    const city = scooter.city as any;
    
    if (city.cityName) {
      return city.cityName;
    }
    
    if (typeof city === 'string') {
      const foundCity = cities.find(c => c._id === city);
      return foundCity?.cityName || t('scooterModal.cityUnknown');
    }
    
    return t('scooterModal.cityUnknown');
  }, [scooter?.city, cities, t]);

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
              borderRadius: '8px'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('scooterModal.technicalInfo')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">ID:</Typography>
                <Typography variant="body2" fontWeight={500}>#{scooter?.name}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">IMEI:</Typography>
                <Typography variant="body2" fontWeight={500}>{scooter?.hex || 'N/A'}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">{t('scooterModal.lastUsed')}:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {scooter?.updatedAt ? moment(scooter.updatedAt).format('DD.MM.YYYY HH:mm') : 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">{t('scooterModal.city')}:</Typography>
                <Typography variant="body2" fontWeight={500}>{cityName}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('scooterModal.status')}:</Typography>
                <Chip
                  label={getStatusLabel(scooter?.status)}
                  size="small"
                  sx={{
                    bgcolor: getStatusColor(scooter?.status),
                    color: '#FFF',
                    fontWeight: 500,
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
              borderRadius: '8px',
              height: '100%'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('scooterModal.usageStats')}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{t('scooterModal.batteryCharge')}:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="#4CAF50">
                    {(cleanBatteryValue(scooter?.battery) ?? 0)}%
                  </Typography>
                  {hasInactivityFlag(scooter?.battery) && (
                    <Chip
                      label={t('scooterModal.inactivity24h')}
                      size="small"
                      sx={{
                        bgcolor: theme.palette.mode === 'light' ? '#FF9800' : '#E65100',
                        color: '#FFF',
                        fontWeight: 500,
                        fontSize: '0.65rem',
                        height: '20px'
                      }}
                    />
                  )}
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(() => {
                  const val = Number(cleanBatteryValue(scooter?.battery) ?? 0);
                  return Number.isNaN(val) ? 0 : val;
                })()} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#4CAF50',
                    borderRadius: 4
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">{t('scooterModal.rating')}:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {renderStars(scooter?.avgRate || 0)}
                <Typography variant="body2" fontWeight={600} sx={{ ml: 1 }}>
                  {(scooter?.avgRate || 0).toFixed(1)}/5.0
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">{t('scooterModal.coordinates')}:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn sx={{ fontSize: '1rem', color: '#F44336' }} />
                <Typography variant="body2" fontWeight={500}>
                  {scooter?.coords?.lat?.toFixed(6) || '0.000000'}, {scooter?.coords?.lon?.toFixed(6) || '0.000000'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                {t('scooterModal.quickActions')}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<VolumeUp />}
                    onClick={onSignal}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                  >
                    {t('scooterModal.signal')}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={scooter?.lock ? <LockOpen /> : <Block />}
                    onClick={onLockToggle}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                  >
                    {scooter?.lock ? t('scooterModal.unlock') : t('scooterModal.lock')}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<RestartAlt />}
                    onClick={onRestart}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                  >
                    {t('scooterModal.restart')}
                  </Button>
                </Grid>
                {onBatteryCover && (
                  <>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<BatteryChargingFull />}
                        onClick={() => onBatteryCover(true)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '8px',
                          borderColor: '#4ECDC4',
                          color: '#4ECDC4',
                          '&:hover': { borderColor: '#3BA99F', bgcolor: 'rgba(78,205,196,0.08)' }
                        }}
                      >
                        {t('scooterModal.batteryOpen')}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<BatteryFull />}
                        onClick={() => onBatteryCover(false)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '8px',
                          borderColor: theme.palette.mode === 'light' ? '#9E9E9E' : '#BDBDBD',
                          color: theme.palette.mode === 'light' ? '#616161' : '#E0E0E0',
                          '&:hover': {
                            borderColor: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                            bgcolor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)'
                          }
                        }}
                      >
                        {t('scooterModal.batteryClose')}
                      </Button>
                    </Grid>
                  </>
                )}
                {isSuperAdmin && (
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      startIcon={<Delete />}
                      onClick={onDelete}
                      sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                      {t('scooterModal.delete')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('scooterModal.mapTitle')}
          </Typography>
          <TextField
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
        </Box>
        <Box sx={{ borderRadius: '8px', overflow: 'hidden' }}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={15}
            options={mapOptions}
          >
            {routePath.length > 1 && (
              <Polyline
                path={routePath}
                options={{
                  strokeColor: '#5073F8',
                  strokeOpacity: 0.8,
                  strokeWeight: 3,
                  geodesic: true
                }}
              />
            )}
            
            {routePath.map((point, idx) => (
              <Marker
                key={idx}
                position={point}
                title={t('scooterModal.routePoint', { n: idx + 1 })}
                icon={{
                  path: 'M0,-8c-4.4,0-8,3.6-8,8s3.6,8,8,8s8-3.6,8-8S4.4,-8,0,-8z M0,2c-3.3,0-6-2.7-6-6s2.7-6,6-6s6,2.7,6,6S3.3,2,0,2z',
                  fillColor: '#5073F8',
                  fillOpacity: 0.6,
                  scale: 0.7,
                  strokeColor: '#fff',
                  strokeWeight: 1
                }}
              />
            ))}

            {scooter?.coords?.lat && scooter?.coords?.lon && (
              <Marker
                position={{ lat: scooter.coords.lat, lng: scooter.coords.lon }}
                title={t('scooterModal.currentLocation')}
                icon={{
                  path: 'M0,-8c-4.4,0-8,3.6-8,8s3.6,8,8,8s8-3.6,8-8S4.4,-8,0,-8z M0,2c-3.3,0-6-2.7-6-6s2.7-6,6-6s6,2.7,6,6S3.3,2,0,2z',
                  fillColor: '#F44336',
                  fillOpacity: 1,
                  scale: 1,
                  strokeColor: '#fff',
                  strokeWeight: 2
                }}
              />
            )}
          </GoogleMap>
        </Box>
        {coordsHistory.length === 0 && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            {t('scooterModal.noMovementData', { date: moment(selectedDate).format('DD.MM.YYYY') })}
          </Typography>
        )}
        {coordsHistory.length > 0 && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            {t('scooterModal.showingTrips', { count: coordsHistory.length, date: moment(selectedDate).format('DD.MM.YYYY') })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default InformationContent;
